import React from "react";
import moment from "moment";

import { FileUpload } from "@/Common/Components/FileUpload/FileUpload";
import { AttachDocumentLink } from "@/Common/Components/IconControls/AttachDocumentLink";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { ClinicianSearchStrategy, PersonSearchAutocompleteCustom } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { DocumentClassModel } from "@/Common/Models/DocumentClassModel";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { DocumentNote } from "@/Documents/Api/Dto/Response/DocumentNote";
import { DocumentPath } from "@/Documents/Api/Dto/Response/DocumentPath";
import { IDocumentCreateEditUIModel } from "@/Documents/Models/UI/IDocumentCreateEditUIModel";
import { DocumentEditDetails, DocumentEditDetailsOperations } from "./DocumentEditDetails";

import img_icon_add_details from "@/Globals/_images/icons/documents/add_details.png";
import img_icon_add_details_open from "@/Globals/_images/icons/documents/add_details_open.png";

interface IDocumentEditProps {
    model: IDocumentCreateEditUIModel;
    documentClasses: Immutable.List<DocumentClassModel>;
    sites: Immutable.List<SiteUIModel>;
    sources: Immutable.List<SearchableItemModelT<string>>;
    descriptions: Immutable.List<SearchableItemModelT<string>>;
    beginUpload: boolean;
}

class DocumentEdit extends React.Component<IDocumentEditProps, never> {
    private operations: DocumentEditDetailsOperations;
    private upload_control_id: string = "edit_doc_uploader";
    private disabled: boolean = true;
    private helper: MyleHelper = new MyleHelper();
    private cliniciansSearchStrategy: ClinicianSearchStrategy;
    private fileUpload: FileUpload;

    constructor(props: IDocumentEditProps) {
        super(props);

        this.initData();
    }

    public render(): JSX.Element {
        var doc = this.props.model.get("MedicalDocumentInfo");
        let docModel = doc.Model;

        var documentSubClassLookupId: string = docModel.SubClassId;
        var documents: DocumentPath[] = docModel.DocumentPathList;
        var documentsRender = documents.map((doc, idx) => {
            var readableFileName = this.helper.GetReadableFileName(doc.FileName);
            var docPath = apiSettings.buildAppUrl(`api/medicalDocuments/downloadFile/${doc.Id}/${encodeURIComponent(readableFileName)}`, { auth: true });

            return (
                <div key={idx + "_doc"} className="margintop5">
                    <a className="pointer" onClick={() => MyleHelper.OpenNewWindow(docPath)}> {readableFileName}</a>
                </div>
            );
        });

        var addDetailstring = cacheStore.GetString("AddDocumentDetailsLabel");
        var hideDetailstring = cacheStore.GetString("HideDocumentDetailsLabel");
        var detailsString: string = this.props.model.get("AdditionalDetailsVisible") ? hideDetailstring : addDetailstring;

        var notes: DocumentNote[] = docModel.NoteInfoCollection;
        var notesRender = notes.map(note => {
            var person = cacheStore.GetEmployeeByContactIdOrEmpty(note.ContactId);
            return (
                <div key={note.NoteId} className="row">
                    <div className="col-lg-6 col-md-9 note-author">
                        <strong>{person.PrefixLookup.GetValue()}
                            {person.LastName}, {person.FirstName}
                        </strong>
                        <div>{note.NoteDate != null ? moment(note.NoteDate).format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + moment(note.NoteDate).format("HH:mm:ss") : ""}</div>
                    </div>
                    <div className="col-lg-6 col-md-9">
                        <span className="cl-note">{note.NoteContent}</span>
                    </div> <hr />
                </div>
            );
        });

        this.disabled = this.props.model.get("IsSaving")
            || !this.props.model.get("SelectedPatients").count()
            || !docModel.ReceiptDate;

        var sites = this.props.sites.toArray();

        var docDetailsRender = this.props.model.get("AdditionalDetailsVisible")
            ? <img src={img_icon_add_details_open} alt={cacheStore.GetString("AddDocumentDetailsLabel")} />
            : <img src={img_icon_add_details} alt={cacheStore.GetString("AddDocumentDetailsLabel")} />;

        var siteRender = null;

        if (sites.length > 1) {
            siteRender = (
                <div className="row qr paddingbottom10">
                    <div className="col-lg-2 col-md-3 col-sm-12 vcenter text-right">
                        <label className="form-label">{cacheStore.GetString("SitesLabel")}</label>
                    </div>
                    <div className="col-lg-6 col-md-9 col-sm-12">
                        <select className="form-control myle-control" onChange={e => this.OnSiteSelectedChange(e)} value={docModel.SiteId}>
                            {sites.map(site => (
                                <option key={site.Id} value={site.Id}>{site.Name}</option>
                            ))}
                        </select>
                    </div>
                </div>
            );
        }

        return (
            <div className="workspace">
                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <p className="header-font-color">{cacheStore.GetString("DocumentsLabel")}</p>
                            <h3 className="thin-font">{cacheStore.GetString("AttachmentLabel")}</h3>
                        </div>
                    </div>
                </div>
                <div id="posref" className="ws-scrollable">
                    <div className="myle-doc-tb main-content myle-form documents">
                        <div className="row qr paddingbottom10">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("PatientsLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 form-field-kendo-component">
                                <PersonSearchAutocompleteCustom
                                    valid={this.props.model.get("SelectedPatients").size > 0}
                                    useBlurEvent={true}
                                    componentId="edit_doc_patients"
                                    selectedItemList={this.props.model.get("SelectedPatients")}
                                    selectionOp={LocalOperations.Documents.Edit_Patient_Selection_Change}
                                    clearOp={LocalOperations.Documents.Edit_Patient_ClearSelection}
                                    focusOnLoad={false}
                                    disabled={this.props.model.get("LockUI")}
                                    closeDropDownAfterSelection={true}
                                />
                            </div>
                        </div>
                        <div className="row qr paddingbottom10">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("ResponsibleProvidersLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 form-field-kendo-component">
                                <PersonSearchAutocompleteCustom
                                    componentId="edit_doc_clinicians"
                                    selectedItemList={this.props.model.get("SelectedClinicians")}
                                    selectionOp={LocalOperations.Documents.Edit_Clinician_Selection_Change}
                                    clearOp={LocalOperations.Documents.Edit_Clinician_remove}
                                    focusOnLoad={false}
                                    disabled={this.props.model.get("LockUI")}
                                    closeDropDownAfterSelection={true}
                                    searchStrategy={this.cliniciansSearchStrategy}
                                    allowMultipleItemSelection={true}
                                />
                            </div>
                        </div>
                        {siteRender}
                        <div className="row qr">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("ClassLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12">
                                <select className="form-control myle-control" onChange={e => this.OnClassSubClassSelectionChange(e)} data-cy="document-edit-class">
                                    {this.props.documentClasses.toArray().map(g => (
                                        <optgroup key={g.LookupId} label={cacheStore.GetLocalizedValueByLookupId(g.LookupId)}>
                                            {g.SubClasses.toArray().map(s => {
                                                let isSelected = (documentSubClassLookupId === s.LookupId);
                                                let formattedOutput = cacheStore.GetLocalizedValueByLookupId(g.LookupId) + " - " + cacheStore.GetLocalizedValueByLookupId(s.LookupId);

                                                return (
                                                    <option
                                                        key={s.LookupId}
                                                        selected={isSelected}
                                                        value={g.LookupId + "&" + s.LookupId}
                                                    >
                                                        {formattedOutput}
                                                    </option>
                                                );
                                            })}
                                        </optgroup>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="row qr paddingbottom10">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("SourceLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 form-field-kendo-component">
                                <TextboxControl disabled={this.props.model.get("LockUI")} autoCompleteItems={this.props.sources} value={docModel.Source} operation={LocalOperations.Documents.Edit_OnSourcesChange} onAutoCompleteSelectCallBackOperation={LocalOperations.Documents.Edit_OnSourcesChange} />
                            </div>
                        </div>
                        <div className="row qr  paddingbottom10">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("DescriptionDLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12 form-field-kendo-component">
                                <TextboxControl disabled={this.props.model.get("LockUI")} autoCompleteItems={this.props.descriptions} value={docModel.Subject} onAutoCompleteSelectCallBackOperation={LocalOperations.Documents.Edit_OnDescriptionChange} operation={LocalOperations.Documents.Edit_OnDescriptionChange} />
                            </div>
                        </div>
                        <div className="row qr  paddingbottom10">
                            <div className="col-lg-2 col-md-3 col-sm-12 vcenter text-right">
                                <label className="form-label">{cacheStore.GetString("DocumentDateLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 form-field-kendo-component">
                                <DateSelector id="doc_create_date" cssClass="" date={docModel.ReceiptDate} onSelectOp={LocalOperations.Documents.Edit_Set_Document_Date} dateFormat="dddd, dd MMMM yyyy" disabled={this.props.model.get("LockUI")} />
                            </div>
                            <div className="col-lg-2 col-md-3 col-sm-12 paddingtop7 marginleft0">
                                <a style={{ marginTop: "9px", cursor: "pointer" }} className="pull-left" id="dp1-today" onClick={e => this.onTodayClick(e)}>{cacheStore.GetString("TodayLabel")}</a>
                            </div>
                        </div>

                        <div className="row qr paddingbottom15">
                            <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                                <label className="form-label">{cacheStore.GetString("DocumentGridFilesLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12">
                                {documentsRender}
                                <FileUpload
                                    ref={ref => this.fileUpload = ref}
                                    componentId={this.upload_control_id}
                                    onErrorOperation={LocalOperations.Documents.Create_UploadFailPatientProfile}
                                    onCompleteOperation={LocalOperations.Documents.Edit_UploadComplete}
                                    onFileSelectionCallback={LocalOperations.Documents.Edit_OnFileSelectionChange}
                                />
                            </div>
                        </div>

                        <div className="row no-side-margin qr paddingbottom10">
                            <div className="col-lg-2 col-md-3 col-sm-12" />
                            <div className="col-lg-6 col-md-9 col-sm-12">
                                <AttachDocumentLink onClick={() => this.SelectFiles()} />
                            </div>
                        </div>

                        <div className="row qr paddingbottom20 paddingtop20">
                            <div className="col-lg-2 col-md-3 col-sm-12" />
                            <div className="col-lg-6 col-md-9 col-sm-12">
                                <a href="#" className="marginleft5 pointer" id="show_additional_details" data-cy="document-edit-showAdditionalDetails" onClick={e => this.showAdditionalDetails(e)}>
                                    <span id="show_additional_text">
                                        {docDetailsRender}
                                        <span className="marginleft5">{detailsString}</span>
                                    </span>
                                </a>
                            </div>
                        </div>
                        <div className="row" style={{ margin: "15px 0" }}>
                            <div className="col-lg-6 col-lg-offset-2 col-md-11 col-md-offset-1">
                                <DocumentEditDetails model={this.props.model} operations={this.operations} disabled={this.props.model.get("LockUI")} />
                            </div>
                        </div>

                        <div className="row qr paddingtop10">
                            <div className="col-lg-2 col-md-3 col-sm-12 text-right">
                                <label className="form-label">{cacheStore.GetString("NotesLabel")}</label>
                            </div>
                            <div className="col-lg-6 col-md-9 col-sm-12">
                                <div>
                                    {notesRender}
                                </div>
                                <textarea onChange={e => this.OnNoteChange(e)} id="doc_edit_note" value={this.props.model.get("Note")} className="form-control" rows={4} />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="ws-footer">
                    <div className="col-lg-12 col-md-12 col-sm-12 text-right">
                        <div className="inline-block push-right-10">
                            <div className="checkbox">
                                <label>
                                    <input name="urgent" id="_doc_urgent" data-cy="document-edit-urgent" value="urgent" checked={docModel.IsUrgent} type="checkbox" onChange={e => this.OnUrgencyCheckChange(e)} />
                                    {cacheStore.GetString("UrgentLabel")}
                                </label>
                            </div>
                        </div>
                        <button onClick={() => this.onCancelClick()} className="btn btn-default push-right" data-cy="document-view-cancel" style={{ marginRight: "5px" }}>{cacheStore.GetString("CancelLabel")}</button>
                        <button
                            disabled={this.disabled}
                            className="btn btn-primary"
                            data-cy="document-view-save"
                            onClick={e => sessionStore.validatePrescriptionRenewalBeforeDocumentSave(docModel.PrescriptionRenewal, docModel.ClassId, () => this.onSaveDocument(e))}
                        >
                            {cacheStore.GetString("SaveLabel")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        let elem = document.getElementById("doc_edit_note");
        autosize(elem);
    }

    public componentWillUnmount(): void {
        var elem = document.getElementById("doc_edit_note");
        var event = document.createEvent("Event");
        event.initEvent("autosize:destroy", true, false);
        elem.dispatchEvent(event);
    }

    public componentDidUpdate(): void {
        if (this.props.beginUpload) {
            window.setTimeout(
                () => $("#" + this.upload_control_id + "_upload-wrapper").find("button.k-upload-selected").click(),
                0
            );
        }
    }

    public showAdditionalDetails(e: React.MouseEvent): void {
        e.preventDefault();

        var operation = new LocalEvent(LocalOperations.Documents.Edit_Document_ShowDetails);
        LocalEventDispatcher.dispatch(operation);
    }

    public onTodayClick(e: React.MouseEvent): void {
        e.preventDefault();

        var operation = new LocalEvent(LocalOperations.Documents.Edit_OnTodayClick);
        LocalEventDispatcher.dispatch(operation);
    }

    public onSaveDocument(e: React.MouseEvent): void {
        e.preventDefault();

        if (this.disabled) return;

        // ensure the method can only be called at most once per render
        this.disabled = true;

        var uploader = $("#" + this.upload_control_id).data("kendoUpload");

        var fileHolder = $("#" + this.upload_control_id + "_upload-wrapper").find("ul.k-upload-files");
        var fileCount = fileHolder.children(".k-file").length;

        // fire uploader if we have files to upload (it will then trigger the document save post when complete

        if (fileCount > 0) {
            uploader.trigger("upload");
            var operation = new LocalEvent(LocalOperations.Documents.Edit_BeginUpload);
            LocalEventDispatcher.dispatch(operation);
        }
        else {
            // if no files to upload, trigger document save post immediately
            var operation = new LocalEvent(LocalOperations.Documents.Edit_OnSaveClick);
            LocalEventDispatcher.dispatch(operation);
        }
    }

    public OnClassSubClassSelectionChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        var selected: string = e.target.value;
        var classes = selected.split("&");
        var operation = new LocalEvent(LocalOperations.Documents.Edit_SubClassChange);
        operation.AddItem("value_class", classes[0]);
        operation.AddItem("value_subclass", classes[1]);

        LocalEventDispatcher.dispatch(operation);
    }

    public OnSiteSelectedChange(e: React.ChangeEvent<HTMLSelectElement>): void {
        var operation = new LocalEvent(LocalOperations.Documents.Edit_SiteChange);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    public OnNoteChange(e: React.ChangeEvent<HTMLTextAreaElement>): void {
        var operation = new LocalEvent(LocalOperations.Documents.Edit_Note_Change);
        operation.AddItem("value", e.target.value);
        LocalEventDispatcher.dispatch(operation);
    }

    public onCancelClick(): void {
        var operation = new LocalEvent(LocalOperations.Documents.Edit_OnCancel);
        LocalEventDispatcher.dispatch(operation);
    }

    public OnUrgencyCheckChange(e: React.ChangeEvent<HTMLInputElement>): void {
        var operation = new LocalEvent(LocalOperations.Documents.Edit_OnUrgencyCheckChange);
        operation.AddItem("value", e.target.checked);
        LocalEventDispatcher.dispatch(operation);
    }

    private SelectFiles(): void {
        this.fileUpload?.openUpload();
    }

    private initData(): void {
        this.operations = new DocumentEditDetailsOperations();
        this.operations.AuthroingDateChangeOperation = LocalOperations.Documents.Edit_AuthoringDateChange;
        this.operations.SendingFacilityIdChangeOperation = LocalOperations.Documents.Edit_SendingFacilityIdChange;
        this.operations.MnemonicChangeOperation = LocalOperations.Documents.Edit_MnemonicChange;
        this.operations.SourceAuthorNameChangeOperation = LocalOperations.Documents.Edit_SourceAuthorNameChange;
        this.operations.ObservationDateChangeOperation = LocalOperations.Documents.Edit_ObservationDateChange;
        this.operations.SendingFacilityReportChangeOperation = LocalOperations.Documents.Edit_SendingFacilityReportChange;
        this.operations.SubClassChangeOperation = LocalOperations.Documents.Edit_AccompanyingSubClassChange;
        this.operations.HRMStatusChangeOperation = LocalOperations.Documents.Edit_HRMStatusChange;
        this.cliniciansSearchStrategy = new ClinicianSearchStrategy(cacheStore.GetClinicians(false));
    }
}

export { DocumentEdit, IDocumentEditProps };
